import { readingHtml } from '../../shared/reading_request';
import AudioRecorder from 'audio-recorder-polyfill'

class PronunciationService {
  constructor(csrfToken, language, dialectCode, owned = null) {
    this.owned = owned;
    this.disposed = false;
    if (owned) {
      this.navigationCleanup = () => this.destroy();
      document.addEventListener('turbolinks:before-cache', this.navigationCleanup);
      document.addEventListener('turbo:before-cache', this.navigationCleanup);
      window.addEventListener('pagehide', this.navigationCleanup);
    }
    this.language = language;
    this.dialectCode = dialectCode;
    this.csrfToken = csrfToken;

    this.isRecording = false;
    this.timer;
    this.mediaRecorder;
    this.audioChunks = [];
  }

  alreadyRecording() {
    return this.isRecording;
  }

  stopPronunciation(btn) {
    clearTimeout(this.timer);

    btn.innerHTML = `<i class="fa fa-microphone"></i> ${I18n.t('javascript.chat.say_it')}`;
    btn.classList.remove('pulse');

    this.isRecording = false;

    if (this.owned) {
      if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.stop();
      else this.operation?.controller.abort();
    }
    else this.mediaRecorder.stop();
  }

  async startPronunciation(btn, text, suggestionIndex) {
    if (this.owned) return this.startOwnedPronunciation(btn, text, suggestionIndex);
    this.emitPronunciationStarted();
    this.isRecording = true;

    btn.innerHTML = `<i class="fa fa-stop"></i> ${I18n.t('javascript.chat.stop_recording')}`;
    btn.classList.add('pulse');
    this.setupTimer(btn, text, suggestionIndex)

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    await this.setupRecordBtn(btn, text, stream, suggestionIndex)

    this.mediaRecorder.start();

  }

  async startOwnedPronunciation(btn, text, suggestionIndex) {
    if (this.disposed || this.isRecording || !this.owned.pronunciationEnabled || ![0, 1].includes(Number(suggestionIndex)) || (this.owned.isCurrent && !this.owned.isCurrent())) return;
    const node = document.getElementById(`js-suggestion-${suggestionIndex}`);
    if (!node?.isConnected) return;
    this.operation?.controller.abort();
    const operation = {controller: new AbortController(), node, button: btn}; this.operation = operation;
    const current = () => !this.disposed && this.operation === operation && !operation.controller.signal.aborted && node.isConnected && (!this.owned.isCurrent || this.owned.isCurrent());
    operation.current = current; this.isRecording = true;
    const reset = () => { btn.innerHTML = `<i class="fa fa-microphone"></i> ${I18n.t('javascript.chat.say_it')}`; btn.classList.remove('pulse'); };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      if (!current()) { stream.getTracks().forEach(track => track.stop()); return; }
      operation.stream = stream;
      this.emitPronunciationStarted();
      const recorder = new AudioRecorder(stream, {sampleRate:16000,channelCount:1,bitsPerSample:16}); this.mediaRecorder = recorder;
      const chunks = [];
      recorder.addEventListener('dataavailable', event => { if (current()) chunks.push(event.data); });
      recorder.addEventListener('stop', async () => {
        clearTimeout(operation.timer); stream.getTracks().forEach(track => track.stop());
        if (this.operation === operation) { this.isRecording = false; reset(); }
        if (!current()) return;
        try {
          const audio = await this.resampleAudio(new Blob(chunks, {type:'audio/wav'}),16000,1);
          if (current()) await this.sendDataToRailsBackend(text,audio,suggestionIndex,operation);
        } catch (_) {
          if (current()) this.updateSuggestionText(suggestionIndex, `<p class="text-danger">${I18n.t('javascript.chat.pronunciation_error')}</p>`, true);
        }
      });
      btn.innerHTML = `<i class="fa fa-stop"></i> ${I18n.t('javascript.chat.stop_recording')}`; btn.classList.add('pulse');
      recorder.start(); this.setupTimer(btn,text,suggestionIndex); operation.timer = this.timer;
    } catch (_) {
      operation.stream?.getTracks().forEach(track => track.stop());
      if (current()) this.updateSuggestionText(suggestionIndex, `<p class="text-danger">${I18n.t('javascript.chat.pronunciation_error')}</p>`, true);
      if (this.operation === operation) { this.isRecording = false; reset(); }
    }
  }

  destroy() {
    if (!this.owned) return;
    this.disposed = true; clearTimeout(this.timer); this.operation?.controller.abort();
    if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.stop();
    this.operation?.stream?.getTracks().forEach(track => track.stop());
    this.isRecording = false;
    document.removeEventListener('turbolinks:before-cache', this.navigationCleanup);
    document.removeEventListener('turbo:before-cache', this.navigationCleanup);
    window.removeEventListener('pagehide', this.navigationCleanup);
  }

  // setup 15 second timer that gets cancelled if user stops recording before 15 seconds
  setupTimer(btn, text, suggestionIndex) {
    const that = this;

    that.timer = setTimeout(function () {
      that.stopPronunciation(btn, text, suggestionIndex);
    }, 45000);
  }


  async setupRecordBtn(btn, text, stream, suggestionIndex) {
    const that = this;

    const audioConfig = {
      sampleRate: 16000,
      channelCount: 1,
      bitsPerSample: 16
    };

    that.mediaRecorder = new AudioRecorder(stream, audioConfig);

    that.mediaRecorder.addEventListener('dataavailable', e => {
      that.audioChunks.push(e.data);
    });

    that.mediaRecorder.addEventListener('stop', async e => {
      const audioBlob = new Blob(that.audioChunks, { type: "audio/wav" });

      // Resample the audio if needed
      const resampledBlob = await this.resampleAudio(audioBlob, 16000, 1);

      this.sendDataToRailsBackend(text, resampledBlob, suggestionIndex)

      that.audioChunks = [];
    });
  }

  async resampleAudio(audioBlob, targetSampleRate, targetChannels) {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const offlineContext = new OfflineAudioContext(
      targetChannels,
      audioBuffer.duration * targetSampleRate,
      targetSampleRate
    );

    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start(0);

    const renderedBuffer = await offlineContext.startRendering();

    const wavBuffer = this.audioBufferToWav(renderedBuffer);
    return new Blob([wavBuffer], { type: 'audio/wav' });
    } finally { if (this.owned) await audioContext.close(); }
  }

  audioBufferToWav(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const bufferLength = buffer.length;
    const byteRate = sampleRate * blockAlign;
    const dataSize = bufferLength * blockAlign;

    const headerSize = 44;
    const wavBuffer = new ArrayBuffer(headerSize + dataSize);
    const view = new DataView(wavBuffer);

    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    const channelData = [];
    for (let i = 0; i < numChannels; i++) {
      channelData.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < bufferLength; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        const sample = channelData[channel][i];
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }

    return wavBuffer;
  }

  sendDataToRailsBackend(text, audioBlob, suggestionIndex, operation = null) {
    if (this.owned && !operation?.current()) return;
    if (this.owned && audioBlob.size > 1440044) {
      this.updateSuggestionText(suggestionIndex, `<p class="text-danger">${I18n.t('javascript.chat.pronunciation_error')}</p>`, true);
      return;
    }
    const formData = new FormData();
    formData.append("audio", audioBlob, "recorded_audio.wav");
    if (this.owned) {
      formData.append('chat_id', this.owned.chatId); formData.append('chat_message_id', this.owned.messageId); formData.append('suggestion_index', String(suggestionIndex));
    } else {
      formData.append("language", this.language); formData.append("dialect_code", this.dialectCode); formData.append("text", text);
    }

    let suggestionDiv = document.getElementById(`js-suggestion-${suggestionIndex}`);

    if (suggestionDiv) {
      this.addChatLoader(suggestionDiv, I18n.t('javascript.chat.analyzing_pronunciation'));
    }

    let timedOut = false;
    const deadline = this.owned ? setTimeout(() => { timedOut = true; operation.controller.abort(); }, 35000) : null;
    return fetch('/langua/text_pronunciation', {
      ...(this.owned ? {signal: operation.controller.signal, redirect: 'error'} : {}),
      method: "POST",
      headers: {
        'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content')
      },
      credentials: 'same-origin',
      body: formData
    }).then(response => {
      if (this.owned && !response.ok) throw new Error('Pronunciation unavailable');
      return response.json();
    }).then(data => {
      if (this.owned && !operation.current()) return;
      if (this.owned && (!Array.isArray(data.score) || data.score.length !== 2 || !Array.isArray(data.score[1]) || ![data.score[0], ...data.score[1]].every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100))) throw new Error('Invalid score');
      if (suggestionDiv) {
        this.removeChatLoader(suggestionDiv);

        // Extract scores
        const overallScore = data['score'][0];
        const wordScores = data['score'][1];

        // Determine the feedback message based on overall score
        let feedbackMessage = '';
        if (overallScore >= 87) {
          feedbackMessage = I18n.t('javascript.chat.pronunciation_excellent');
        } else if (overallScore >= 78) {
          feedbackMessage = I18n.t('javascript.chat.pronunciation_good');
        } else {
          feedbackMessage = I18n.t('javascript.chat.pronunciation_try_again');
        }

        let feedbackHTML = `<span class="mb-2"><strong>${feedbackMessage}</strong></span><br>`;

        // Split the text into words
        let words = text.split(' ');

        // Ensure we have the same number of words and scores
        if (words.length === wordScores.length) {
          words.forEach((word, index) => {
            let wordScore = wordScores[index];
            let icon = '';
            let color = '';
            if (wordScore >= 75) {
              icon = 'fa-check-circle';
              color = 'green';
            } else {
              icon = 'fa-exclamation-circle';
              color = 'orange';
            }
            feedbackHTML += `<span style="color: ${color};"><i class="fa ${icon}"></i> ${this.owned ? readingHtml(word) : word} </span>`;
          });

          feedbackHTML += `<br><span class="mt-2"><small class="text-muted">${I18n.t('javascript.chat.pronunciation_beta_notice')}</small></span>`;
        } else {
          console.error('Mismatch between number of words and scores');
        }

        this.updateSuggestionText(suggestionIndex, feedbackHTML, false);
      }
    }).catch(error => {
      if (this.owned && !operation.current() && !(timedOut && !this.disposed && this.operation === operation && operation.node.isConnected && (!this.owned.isCurrent || this.owned.isCurrent()))) return;
      if (!this.owned) console.log("Error in sending audio: ", error);
      if (suggestionDiv) {
        this.removeChatLoader(suggestionDiv);
        this.updateSuggestionText(suggestionIndex, `<p class="text-danger">${I18n.t('javascript.chat.pronunciation_error')}</p>`, true);
      }
    }).finally(() => clearTimeout(deadline));
  }

  emitPronunciationStarted() {
    // Emit the playended event
    const event = new CustomEvent('chat:pronounciationstarted');
    document.dispatchEvent(event);
  }

  // Function to add the loader
  addChatLoader(targetElement, message) {
    const loader = document.createElement('div');
    loader.className = 'pronunciation-loader mt-2';

    // display message if provided
    loader.innerHTML = `
          <p class="text-center">${message}</p>
    `;

    targetElement.appendChild(loader);
  }

  // Function to remove the loader
  removeChatLoader(targetElement) {
    const loader = targetElement.querySelector('.pronunciation-loader');
    if (loader) {
      targetElement.removeChild(loader);
    }

    this.isLoadingMessage = false;
  }

  updateSuggestionText(suggestionIndex, newText, isError) {
    const suggestionDiv = $(`#js-suggestion-${suggestionIndex}`);
    if (!suggestionDiv.data('original-text')) {
      suggestionDiv.data('original-text', suggestionDiv.html());
    }
    if (isError) {
      suggestionDiv.append(newText);
    } else {
      suggestionDiv.html(newText);
    }
    suggestionDiv.data('showing-pronunciation', true);
  }
}

export default PronunciationService;
